import React, { useEffect, useState } from 'react';
import { API_BASE, authHeaders } from '../../utils/api';
import '../../styles/common.css';
import './EnquireNow.css';

const EnquireNow = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [courses, setCourses] = useState([]);
  const [settings, setSettings] = useState({ formEnabled: true });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userRole, setUserRole] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [courseForm, setCourseForm] = useState({ name: '', status: 'pending' });
  const [editingCourse, setEditingCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('courses');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || '');
      } catch (e) {
        console.error('Error parsing token', e);
      }
    }
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [enquiriesRes, coursesRes, settingsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/enquiries${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/admin/enquiries/courses`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/admin/enquiries/settings`, { headers: authHeaders() })
      ]);

      const enquiriesData = await enquiriesRes.json();
      const coursesData = await coursesRes.json();
      const settingsData = await settingsRes.json();

      if (enquiriesRes.ok) setEnquiries(enquiriesData.data || []);
      if (coursesRes.ok) setCourses(coursesData.data || []);
      if (settingsRes.ok) setSettings(settingsData.data || { formEnabled: true });
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleFormStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/enquiries/settings/toggle-form`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess('Form status updated');
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    if (!courseForm.name.trim()) {
      setError('Course name is required');
      return;
    }

    try {
      const url = editingCourse 
        ? `${API_BASE}/api/admin/enquiries/courses/${editingCourse}`
        : `${API_BASE}/api/admin/enquiries/courses`;
      const method = editingCourse ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(courseForm)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      
      setSuccess(editingCourse ? 'Course updated' : 'Course added');
      setCourseForm({ name: '', status: 'pending' });
      setEditingCourse(null);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCourseAction = async (id, action) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/enquiries/courses/${id}/${action}`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess(`Course ${action}d successfully`);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEnquiryAction = async (id, action) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/enquiries/${id}/${action}`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess(data.message);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendEmail = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/enquiries/${id}/send-email`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess(data.message);
    } catch (err) {
      setError(err.message);
    }
  };

  const editCourse = (course) => {
    setCourseForm({ name: course.name, status: course.status });
    setEditingCourse(course.id);
  };

  const resetCourseForm = () => {
    setCourseForm({ name: '', status: 'pending' });
    setEditingCourse(null);
  };

  const handleExportEnquiries = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/enquiries/export/csv`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `enquiries_${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
      setSuccess('Exported successfully');
    } catch (err) { setError(err.message); }
  };

  return (
    <div className="admin-page">
      <div className="page-heading">
        <h1>Enquire Now Management</h1>
        <p>Manage course enquiries, form settings, and available courses.</p>
      </div>

      {error && <div className="admin-alert error">{error}</div>}
      {success && <div className="admin-alert success">{success}</div>}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-btn ${activeTab === 'courses' ? 'active' : ''}`}
          onClick={() => setActiveTab('courses')}
        >
          Course Management
        </button>
        <button 
          className={`tab-btn ${activeTab === 'enquiries' ? 'active' : ''}`}
          onClick={() => setActiveTab('enquiries')}
        >
          Student Enquiries
        </button>
        <button 
          className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Form Settings
        </button>
      </div>

      {/* Course Management Tab */}
      {activeTab === 'courses' && (
        <div className="tab-content">
          <div className="content-section">
            <h2>{editingCourse ? 'Edit Course' : 'Add New Course'}</h2>
            <form className="form-horizontal" onSubmit={handleCourseSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Course Name *</label>
                  <input
                    type="text"
                    value={courseForm.name}
                    onChange={(e) => setCourseForm({...courseForm, name: e.target.value})}
                    required
                    placeholder="e.g., B.Tech in Marine Engineering"
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={courseForm.status}
                    onChange={(e) => setCourseForm({...courseForm, status: e.target.value})}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    {editingCourse ? 'Update Course' : 'Add Course'}
                  </button>
                  {editingCourse && (
                    <button type="button" className="btn-secondary" onClick={resetCourseForm}>
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>

          <div className="content-section">
            <h2>Available Courses</h2>
            <div className="table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Course Name</th>
                    <th>Status</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map((course) => (
                    <tr key={course.id}>
                      <td>{course.name}</td>
                      <td><span className={`status status-${course.status}`}>{course.status}</span></td>
                      <td>{course.created_at ? new Date(course.created_at).toLocaleDateString() : 'N/A'}</td>
                      <td className="actions">
                        <button className="btn-edit" onClick={() => editCourse(course)}>✏️ Edit</button>
                        {course.status !== 'approved' && (
                          <button className="btn-success" onClick={() => handleCourseAction(course.id, 'approve')}>✅ Approve</button>
                        )}
                        {course.status !== 'rejected' && (
                          <button className="btn-danger" onClick={() => handleCourseAction(course.id, 'reject')}>❌ Reject</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Enquiries Tab */}
      {activeTab === 'enquiries' && (
        <div className="tab-content">
          <div className="content-section">
            <div className="section-header">
              <h2>Student Enquiries</h2>
              <div className="filter-controls" style={{display:'flex',gap:'10px',alignItems:'center'}}>
                <button onClick={handleExportEnquiries} className="btn-secondary">📥 Export CSV</button>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All Enquiries</option>
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="enrolled">Enrolled</option>
                  <option value="rejected">Not Interested</option>
                </select>
              </div>
            </div>
            
            <div className="table-container">
              {loading ? (
                <div className="loading-state">Loading enquiries...</div>
              ) : enquiries.length === 0 ? (
                <div className="empty-state">No enquiries found.</div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Email</th>
                      <th>Course</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enquiries.map((enquiry) => (
                      <tr key={enquiry.id}>
                        <td>{enquiry.name}</td>
                        <td>{enquiry.mobile}</td>
                        <td>{enquiry.email}</td>
                        <td>{enquiry.course}</td>
                        <td>{new Date(enquiry.created_at).toLocaleDateString()}</td>
                        <td><span className={`status status-${enquiry.status}`}>{enquiry.status}</span></td>
                        <td className="actions">
                          <button className="btn-info" onClick={() => handleEnquiryAction(enquiry.id, 'contact')}>📞 Contact</button>
                          <button className="btn-success" onClick={() => handleEnquiryAction(enquiry.id, 'enroll')}>✅ Enroll</button>
                          <button className="btn-danger" onClick={() => handleEnquiryAction(enquiry.id, 'reject')}>❌ Reject</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="tab-content">
          <div className="content-section">
            <h2>Form Settings</h2>
            <div className="settings-card">
              <div className="setting-item">
                <div className="setting-info">
                  <h3>Enquiry Form Status</h3>
                  <p>Enable or disable the public enquiry form on the website and sticky button</p>
                  <p><strong>Current Status:</strong> <span className={`status status-${settings.formEnabled ? 'approved' : 'rejected'}`}>{settings.formEnabled ? 'Enabled' : 'Disabled'}</span></p>
                </div>
                <div className="setting-control">
                  {settings.formEnabled ? (
                    <button className="btn-danger" onClick={toggleFormStatus}>
                      🚫 Disable Form
                    </button>
                  ) : (
                    <button className="btn-success" onClick={toggleFormStatus}>
                      ✅ Enable Form
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnquireNow;